import { inject, Injectable } from '@angular/core';
import { PlanType } from '@app/+authenticated/+reports/shared/subscriptions/subscription.model';
import { AppState } from '@app/reducers';
import { getAccountSubscription } from '@app/reducers/account/account.service';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { getSelectedSchedulePeriod } from '@app/reducers/page-filters/page-filters.helper';
import { hasPermissionViewScheduleTargets } from '@app/reducers/page-filters/page-filters.service';
import { getSelectedDepartmentIds } from '@app/reducers/selected-departments/selected-departments.service';
import { hasAtleastSubscriptionPlan } from '@app/shared/subscription-plan/subscription-plan.directive';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { filter, map, pipe, switchMap, tap, withLatestFrom } from 'rxjs';

import { InsightsScheduleResponse } from './insights-schedule.model';
import { InsightsService } from './insights.service';

// Idea: do we want to display loading state
interface ScheduleTargetInsightsState {
  insights: InsightsScheduleResponse | null;
  isLoading: boolean;
}

const initialState: ScheduleTargetInsightsState = {
  insights: null,
  isLoading: false,
};

@Injectable({
  providedIn: 'root',
})
export class ScheduleTargetInsightsStore {
  readonly #insightsService = inject(InsightsService);
  readonly #state = signalState(initialState);
  readonly #store: Store<AppState> = inject(Store);

  readonly #hasPlanRequirement = this.#store
    .select(getAccountSubscription)
    .pipe(map((accountSubscription) => hasAtleastSubscriptionPlan(PlanType.PREMIUM, accountSubscription)));

  readonly #allowedDepartmentIds = this.#store.select(getSelectedDepartmentIds).pipe(
    withLatestFrom(this.#store.select(getPermissionState)),
    map(([departmentIds, permissionState]) =>
      departmentIds.filter((id) => hasPermissionViewScheduleTargets(permissionState, id)),
    ),
  );

  public readonly insights = this.#state.insights;

  readonly #loadInsights = rxMethod<void>(
    pipe(
      withLatestFrom(
        this.#allowedDepartmentIds,
        this.#store.select(getSelectedSchedulePeriod),
        this.#hasPlanRequirement,
      ),
      filter(
        ([, departmentIds, period, hasPlanRequirement]) =>
          hasPlanRequirement && departmentIds?.length > 0 && !!period?.minDate && !!period?.maxDate,
      ),
      tap(() => patchState(this.#state, { isLoading: true })),
      switchMap(([, departmentIds, period]) =>
        this.#insightsService
          .getScheduleInsights({
            departmentIds,
            from: period.minDate,
            to: period.maxDate,
          })
          .pipe(
            tapResponse({
              next: (insights) => {
                patchState(this.#state, { insights });
              },
              error: console.error,
              finalize: () => patchState(this.#state, { isLoading: false }),
            }),
          ),
      ),
    ),
  );

  public loadInsights(): void {
    this.#loadInsights();
  }

  public resetInsights(): void {
    patchState(this.#state, { insights: null });
  }
}
